import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'grafito_flash_promo_expires';
export const FLASH_DWELL_MS = 20 * 1000;
export const FLASH_WINDOW_MS = 10 * 60 * 1000;

function readExpires() {
  if (typeof window === 'undefined') return null;
  try {
    const value = Number(window.localStorage.getItem(STORAGE_KEY));
    if (Number.isFinite(value) && value > Date.now()) return value;
  } catch {
    // storage blocked
  }
  return null;
}

function writeExpires(timestamp) {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(timestamp));
  } catch {
    // storage blocked
  }
}

function clearExpires() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage blocked
  }
}

const FlashPromoContext = createContext(null);

export function FlashPromoProvider({ children }) {
  const [expiresAt, setExpiresAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setExpiresAt(readExpires());
  }, []);

  const active = Boolean(expiresAt && expiresAt > now);
  const remainingMs = active ? expiresAt - now : 0;

  useEffect(() => {
    if (!expiresAt) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [expiresAt]);

  useEffect(() => {
    if (expiresAt && expiresAt <= now) {
      setExpiresAt(null);
      clearExpires();
    }
  }, [expiresAt, now]);

  const unlock = useCallback(() => {
    setExpiresAt((current) => {
      if (current && current > Date.now()) return current;
      const until = Date.now() + FLASH_WINDOW_MS;
      writeExpires(until);
      return until;
    });
    setNow(Date.now());
  }, []);

  return (
    <FlashPromoContext.Provider value={{ active, remainingMs, unlock }}>
      {children}
    </FlashPromoContext.Provider>
  );
}

export function useFlashPromo() {
  const ctx = useContext(FlashPromoContext);
  if (!ctx) return { active: false, remainingMs: 0, unlock: () => {} };
  return ctx;
}

export function useFlashDwell(enabled) {
  const { active, unlock } = useFlashPromo();

  useEffect(() => {
    if (!enabled || active || typeof window === 'undefined') return undefined;

    let elapsed = 0;
    let last = Date.now();
    const timer = setInterval(() => {
      const tick = Date.now();
      if (document.visibilityState === 'visible') elapsed += tick - last;
      last = tick;
      if (elapsed >= FLASH_DWELL_MS) {
        unlock();
        clearInterval(timer);
      }
    }, 200);

    return () => clearInterval(timer);
  }, [enabled, active, unlock]);
}

export function formatCountdown(ms) {
  const totalSeconds = Math.max(0, Math.ceil(Number(ms || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
